import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";
import LoadingBlack from "../../images/loading-black.svg";
import RoleCard from "../../components/roles/role_card";
import Tabs from "../../components/Tabs";
import InterviewJourneyHeader from "../../components/InterviewJourneyHeader";
import UpdateStatusModal from "../../components/modals/UpdateStatusModal";
import HelpPanel from "../../components/HelpPanel";
import { useAuthRedirect } from "../../hooks/useAuthRedirect";
import { startSessionReplay, stopSessionReplay } from "../../lib/analytics";
import { clearInterviewsCache } from "../../lib/interviewsClient";

const INTERVIEWS_CACHE_KEY_PREFIX = "candidate_interviews_";
const INTERVIEWS_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

const VALID_INTERVIEW_STAGES = [
  "Application pending",
  "Match requested",
  "Interviewing",
  "Offer made",
  "Rejected @ CV",
  "Rejected @ Interview",
  "Offer rejected",
  "Offer retracted",
  "Startup withdrawn",
  "Candidate withdrawn",
  "Rejected Interview Request",
];

const mapInterviewsToRoleOptions = (interviewRecords = []) => {
  return (interviewRecords || [])
    .filter(
      (interview) =>
        VALID_INTERVIEW_STAGES.includes(interview?.fields?.["Stage"])
    )
    .map((interview) => {
      const fields = interview.fields || {};

      const roleCoverImage =
        fields["Role Cover Image (from Startup) (from Role)"]?.[0]?.url ||
        "https://placehold.co/400x300/e2e8f0/64748b?text=No+Image";

      // Spotlight fields: These are parallel arrays indexed together
      const spotlightDropbox =
        fields["URL Dropbox cleaned (from Spotlight) (from Role)"] || [];
      const spotlightLoom =
        fields["URL Loom cleaned (from Spotlight) (from Role)"] || [];
      const spotlightTypes =
        fields["Type (from Spotlight) (from Role)"] || [];

      // Build spotlight details by creating separate entries for each URL
      // Each Dropbox and Loom URL should be its own spotlight entry
      const spotlightDetails = [];

      // Process Dropbox URLs
      if (Array.isArray(spotlightDropbox)) {
        spotlightDropbox.forEach((dropboxUrl, index) => {
          if (dropboxUrl) {
            const type = Array.isArray(spotlightTypes) && spotlightTypes[index]
              ? spotlightTypes[index]
              : "Company information"; // Default to Company information if type is missing

            spotlightDetails.push({
              Type: type,
              "URL Dropbox cleaned": dropboxUrl,
              "URL Loom cleaned": null,
              Context: "",
            });
          }
        });
      }

      // Process Loom URLs
      if (Array.isArray(spotlightLoom)) {
        spotlightLoom.forEach((loomUrl, index) => {
          if (loomUrl) {
            const type = Array.isArray(spotlightTypes) && spotlightTypes[index]
              ? spotlightTypes[index]
              : "Company information"; // Default to Company information if type is missing

            spotlightDetails.push({
              Type: type,
              "URL Dropbox cleaned": null,
              "URL Loom cleaned": loomUrl,
              Context: "",
            });
          }
        });
      }

      // Deduplicate spotlights based on unique URL combinations
      // This prevents duplicate spotlight containers from appearing
      const uniqueSpotlights = [];
      const seenCombinations = new Set();

      for (const spotlight of spotlightDetails) {
        // Create a unique key based on both URLs and type
        const key = `${spotlight["Type"]}_${spotlight["URL Dropbox cleaned"] || ''}_${spotlight["URL Loom cleaned"] || ''}`;
        if (!seenCombinations.has(key)) {
          seenCombinations.add(key);
          uniqueSpotlights.push(spotlight);
        }
      }

      // DEBUG: Log spotlight details to diagnose duplication
      if (uniqueSpotlights.length > 0) {
        console.log("DEBUG - Spotlight Details for role:", fields["Role Title (from Role)"], {
          originalCount: spotlightDetails.length,
          uniqueCount: uniqueSpotlights.length,
          details: uniqueSpotlights,
          rawDropbox: spotlightDropbox,
          rawLoom: spotlightLoom,
          rawTypes: spotlightTypes
        });
      }

      return {
        id: interview.id,
        recordId: interview.id,
        airtableId: interview.id,
        fieldId: fields.id,
        "Role Title": fields["Role Title (from Role)"] || "No Title",
        "URL ID": fields["URL ID (from Role)"] || "",
        Compensation: fields["Compensation (from Role)"],
        "Funding Stage (from Startup)":
          fields["Funding Stage (from Startup) (from Role)"] || "",
        "Team Size (from Startup)":
          fields["Team Size (from Startup) (from Role)"] || "",
        "Location (from Startup)":
          fields["Location (from Startup) (from Role)"] || "",
        "Role Cover Image (from Startup)": roleCoverImage,
        "Name (from Startup)": fields["Name (from Startup) (from Role)"] || "",
        "Startup Description (from Startup)":
          fields["Startup Description (from Startup) (from Role)"] || "",
        "Website (from Startup)":
          fields["Website (from Startup) (from Role)"] || "",
        "Specific Experience": fields["Specific Experience (from Role)"] || "",
        "Interview Process": fields["Interview Process (from Role)"] || "",
        "Job Description PDF": fields["Job Description PDF (from Role)"] || "",
        "Working Style": fields["Working Style (from Role)"] || "",
        stage: fields["Stage"],
        "Candidate Interview Stage": fields["Candidate Interview Stage"],
        "HL Calendly Link (if applicable)":
          fields["HL Calendly Link (if applicable)"],
        _debug_spotlights: fields["Spotlight (from Role)"],
        _debug_type: fields["Type (from Spotlight) (from Role)"],
        _debug_dropbox:
          fields["URL Dropbox cleaned (from Spotlight) (from Role)"],
        _debug_loom: fields["URL Loom cleaned (from Spotlight) (from Role)"],
        _debug_context: fields["Spotlight Context (from Role)"],
        spotlightDetails: uniqueSpotlights,
      };
    });
};

// Defensive useQuery hook
function useQuery() {
  const location = useLocation(); // React Router hook
  try {
    // Ensure location.search is a string before passing to URLSearchParams
    const search = (location && typeof location.search === 'string') ? location.search : '';
    return new URLSearchParams(search);
  } catch (error) {
    console.error("Error in useQuery:", error);
    // Return an empty URLSearchParams object in case of an error
    return new URLSearchParams("");
  }
}

function InProcess() {
  const query = useQuery();
  const stream = localStorage.getItem("stream");
  const navigate = useNavigate();
  const id = query.get("id");
  const filterStageParam = query.get("filterStage");

  const { isAuthenticated, isChecking } = useAuthRedirect();

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [filterStage, setFilterStage] = useState(filterStageParam || "All Applications");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [hasSetInitialTab, setHasSetInitialTab] = useState(!!filterStageParam);
  const [stageCounts, setStageCounts] = useState({
    "All Applications": 0,
    Interviewing: 0,
    "Offer made": 0,
    "Application pending": 0,
    "Match requested": 0,
    Unsuccessful: 0,
  });

  // Add candidate data state
  const [candidateData, setCandidateData] = useState(null);
  const [hasBookedFirstCall, setHasBookedFirstCall] = useState(false);

  // State for Update Status Modal
  const [updateStatusModal, setUpdateStatusModal] = useState({
    isOpen: false,
    interview: null
  });

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleStageFilterChange = (value) => {
    setFilterStage(value);
  };

  // Function to refresh interviews data
  const refreshInterviews = useCallback(() => {
    clearInterviewsCache();
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Handler for opening the update status modal
  const handleOpenUpdateStatus = (interview) => {
    setUpdateStatusModal({
      isOpen: true,
      interview: interview
    });
  };

  // Handler for closing the update status modal
  const handleCloseUpdateStatus = () => {
    setUpdateStatusModal({
      isOpen: false,
      interview: null
    });
  };

  // Handler for when status is successfully updated
  const handleStatusUpdate = () => {
    refreshInterviews(); // Refresh the interviews data
  };

  // Fetch candidate data
  const fetchCandidateData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return;
      }

      const response = await axios.get("/api/candidates/fetchCandidate", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const candidateFields = response.data.candidate.fields;

      // Set candidate data for personalization
      let streamValue = candidateFields["Do not edit - new Stream (for visibility column)"] || "";
      if (Array.isArray(streamValue)) {
        streamValue = streamValue.length > 0 ? streamValue[0] : "";
      }

      const candidateInfo = {
        stream: streamValue,
        minimum_salary: candidateFields["Minimum salary"] || "",
        ideal_salary: candidateFields["Ideal salary"] || "",
        working_style: candidateFields["Preferred working style copy"] || [],
        work_experience_1_role: candidateFields["(1) Work Experience Role"] || "",
        work_experience_2_role: candidateFields["(2) Work Experience Role"] || "",
        industries: candidateFields["Industries most interested in copy"] || [],
        hasBookedFirstCall: candidateFields["booked first call (platform test)"] || false,
      };
      setCandidateData(candidateInfo);
      setHasBookedFirstCall(candidateInfo.hasBookedFirstCall);
      console.log('in_process - candidateInfo.stream:', candidateInfo.stream);

    } catch (error) {
      console.error("Error fetching candidate data:", error);
    }
  };

  // Handler for booking calls
  const handleBookCall = () => {
    setHasBookedFirstCall(true);
  };

  // Activate Session Replay and fetch candidate data on mount
  useEffect(() => {
    try { startSessionReplay(); } catch (_) { }
    return () => { try { stopSessionReplay(); } catch (_) { } };
  }, []);

  // Fetch candidate data on mount
  useEffect(() => {
    if (!isAuthenticated || isChecking) return;
    fetchCandidateData();
  }, [isAuthenticated, isChecking]);

  // Update filter stage when URL parameter changes
  useEffect(() => {
    if (filterStageParam && filterStageParam !== filterStage) {
      setFilterStage(filterStageParam);
    }
  }, [filterStageParam, filterStage]);

  useEffect(() => {
    if (!isAuthenticated || isChecking) return;

    const fetchInterviews = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          return;
        }

        const cacheKey = `${INTERVIEWS_CACHE_KEY_PREFIX}${token.slice(0, 32)}`;
        let cachedRawInterviews = null;
        const cachedData = sessionStorage.getItem(cacheKey);

        if (cachedData) {
          try {
            const parsed = JSON.parse(cachedData);
            if (Date.now() - parsed.timestamp < INTERVIEWS_CACHE_TTL) {
              cachedRawInterviews = parsed.data || parsed.interviews || [];
              console.log("Using cached interview data (shared)");
            }
          } catch (e) {
            sessionStorage.removeItem(cacheKey);
          }
        }

        if (cachedRawInterviews) {
          const roleOptions = mapInterviewsToRoleOptions(cachedRawInterviews);
          setInterviews(roleOptions);
          setLoading(false);
          return;
        }

        setLoading(true);
        const response = await axios.get("/api/interviews/fetchInterviews", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const rawInterviews = response.data.interviews || [];
        const roleOptions = mapInterviewsToRoleOptions(rawInterviews);

        if (roleOptions.length > 0) {
          console.log("First interview role image data:", {
            roleTitle: roleOptions[0]["Role Title"],
            imagePath: roleOptions[0]["Role Cover Image (from Startup)"],
            imageType: typeof roleOptions[0]["Role Cover Image (from Startup)"],
            isArray: Array.isArray(
              roleOptions[0]["Role Cover Image (from Startup)"]
            ),
          });
        }

        setInterviews(roleOptions);

        try {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({
              timestamp: Date.now(),
              data: rawInterviews,
            })
          );
        } catch (e) {
          console.error("Failed to cache interviews", e);
        }
      } catch (err) {
        console.error("Error fetching interviews:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInterviews();
  }, [stream, refreshTrigger, isAuthenticated, isChecking]);

  // Calculate stage counts when interviews data changes
  useEffect(() => {
    const counts = {
      "All Applications": 0,
      Interviewing: 0,
      "Offer made": 0,
      "Application pending": 0,
      "Match requested": 0,
      Unsuccessful: 0,
    };
    const unsuccessfulStages = [
      "Rejected @ CV",
      "Rejected @ Interview",
      "Offer rejected",
      "Offer retracted",
      "Startup withdrawn",
      "Candidate withdrawn",
      "Rejected Interview Request",
    ];

    interviews.forEach(interview => {
      // Count all applications
      counts["All Applications"]++;

      if (unsuccessfulStages.includes(interview.stage)) {
        counts.Unsuccessful++;
      } else if (interview.stage === "Interviewing") {
        counts.Interviewing++;
      } else if (interview.stage === "Offer made") {
        counts["Offer made"]++;
      } else if (interview.stage === "Application pending") {
        counts["Application pending"]++;
      } else if (interview.stage === "Match requested") {
        counts["Match requested"]++;
      }
    });

    setStageCounts(counts);

    // Set initial tab based on match requests (only if no URL parameter was provided)
    if (!hasSetInitialTab && interviews.length > 0) {
      if (counts["Match requested"] > 0) {
        setFilterStage("Match requested");
      } else {
        setFilterStage("All Applications");
      }
      setHasSetInitialTab(true);
    }
  }, [interviews, hasSetInitialTab]);

  // Memoize tabOptions to include dynamic counts
  const tabOptions = useMemo(() => [
    { label: "All Applications", value: "All Applications", count: stageCounts["All Applications"] },
    { label: "Interviewing", value: "Interviewing", count: stageCounts.Interviewing },
    { label: "Pending", value: "Application pending", count: stageCounts["Application pending"] },
    { label: "Interview Requests", value: "Match requested", count: stageCounts["Match requested"] },
    { label: "Offer made", value: "Offer made", count: stageCounts["Offer made"] },
    { label: "Unsuccessful", value: "Unsuccessful", count: stageCounts.Unsuccessful },
  ], [stageCounts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <img src={LoadingBlack} alt="Loading" className="w-8 h-8" />
      </div>
    );
  }

  const filteredInterviews = interviews.filter((interview) => {
    const roleTitle = String(interview["Role Title"] || "").toLowerCase();
    const queryText = searchQuery.toLowerCase();

    // If "All Applications" is selected, show all interviews that match the search query
    if (filterStage === "All Applications") {
      return roleTitle.includes(queryText);
    }

    if (filterStage === "Unsuccessful") {
      const unsuccessfulStages = [
        "Rejected @ CV",
        "Rejected @ Interview",
        "Offer rejected",
        "Offer retracted",
        "Startup withdrawn",
        "Candidate withdrawn",
        "Rejected Interview Request",
      ];
      return (
        roleTitle.includes(queryText) &&
        unsuccessfulStages.includes(interview.stage)
      );
    }
    if (filterStage === "Match requested") {
      return roleTitle.includes(queryText) && interview.stage === "Match requested";
    }
    return roleTitle.includes(queryText) && interview.stage === filterStage;
  });

  // Handler for interview action completion
  const handleInterviewActionComplete = () => {
    refreshInterviews();
  };

  return (
    <div className="bg-gray-50 min-h-screen">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <InterviewJourneyHeader stream={stream} navigate={navigate} />

        {/* Your Applications Title Section */}
        <div className="my-8 px-1">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Your Applications</h2>
          <p className="mt-1 text-sm text-gray-500">Track your startup journey and manage interviews</p>
        </div>

        {/* Tabs Section */}
        <div className="mb-6">
          <Tabs
            tabs={tabOptions}
            activeTab={filterStage}
            onTabClick={handleStageFilterChange}
          />
        </div>

        {/* Search Section - moved below tabs */}
        <div className="mb-8">
          <div className="relative flex-grow">
            <input
              type="text"
              className="w-full px-4 py-2.5 pl-11 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-[#02B491]/20 focus:border-[#02B491] transition-all"
              placeholder="Search through your interviews..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Results Section */}
        {filteredInterviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInterviews.map((role, index) => (
              <RoleCard
                key={`${role.id}-${index}-${refreshTrigger}`}
                role={role}
                currentlyPlaying={currentlyPlaying}
                setCurrentlyPlaying={setCurrentlyPlaying}
                showSaveButton={false}
                showApplyNowButton={false}
                isMatchRequest={filterStage === "Match requested"}
                onInterviewActionComplete={handleInterviewActionComplete}
                showUpdateStatusButton={false}
                onUpdateStatus={null}
                currentFilterStage={filterStage}
                candidateData={candidateData}
                onBookCall={handleBookCall}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No interviews found
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              You don't have any interviews in process for this stage.
            </p>
          </div>
        )}
      </div>

      {/* Update Status Modal */}
      <UpdateStatusModal
        isOpen={updateStatusModal.isOpen}
        onClose={handleCloseUpdateStatus}
        interview={updateStatusModal.interview}
        onStatusUpdate={handleStatusUpdate}
      />

      {/* Help Panel */}
      <HelpPanel candidateStream={stream} />
    </div>
  );
}

export default InProcess;
